import * as React from 'react'
import { useUser } from './useUser'
import { useSession } from './useSession'
import { enrollDevice, sendDeviceHeartbeat, DEVICE_HEARTBEAT_INTERVAL_MS, DeviceEnrollmentResponse, getDeviceId } from './device'
import { loadWhoami } from './whoami'
import { redirectToLoginOnce, clearLoginRedirectMark } from './privateAccess'
import { loadDomainConfiguration } from '../config/configApi'
import { loadListManifest, resetListManifest } from '../lists/store'
import { setTelemetryUser, trackError } from '../telemetry/faro'

/**
 * Which half of the startup handshake did not come back usable. Both are hard
 * requirements — see {@link AuthState.failed} — so either one puts the shell on the
 * error page instead of the app.
 */
export type StartupFailure = 'whoami' | 'configuration' | 'unknown'

/**
 * A startup that stopped without anything going wrong. Kept out of
 * {@link StartupFailure} deliberately — it reaches the same page, but it is not a
 * fault, is not reported to telemetry, and offers signing in rather than a reload.
 *
 * `private`: the library is members-only and the visitor is anonymous, and we could
 * not send them to the sign-in app (see `privateAccess.ts` — the loop guard held).
 */
export type StartupBlock = 'private'

interface AuthState {
  /** The signed-in user, or null once we know the visitor is anonymous. */
  user: ReturnType<typeof useUser>[0];
  /**
   * Whether the startup handshake has finished — `whoami` and the domain configuration
   * both answered AND, for a signed-in visitor, device enrollment settled. Guards must
   * wait for this before acting on `!user` — redirecting an authenticated visitor during
   * the in-flight window (user still null) would bounce them off their own protected page.
   *
   * "Finished" is not "succeeded": a failed handshake also flips this true, with
   * {@link failed} set. Read them in that order.
   */
  ready: boolean;
  /**
   * The device-enrollment response for the signed-in visitor — null when anonymous
   * or when enrollment failed. Exposed so the shell can gate first paint on it.
   */
  device: DeviceEnrollmentResponse | null;
  /**
   * Non-null once the handshake settled *badly* — the shell then shows the startup
   * error page and never mounts the app.
   *
   * Two calls decide whether this build can run at all, so neither is allowed to fail
   * quietly:
   *
   * - **`whoami`** must answer 200 (a session) or 403 (a known anonymous visitor).
   *   Anything else — a 500, a proxy error, a dead network (status 0) — means we do
   *   not know who the visitor is, and the old behaviour of defaulting to "anonymous"
   *   silently signed people out of their own account for the length of an outage.
   * - **the domain configuration** must resolve. Its `csrf_token` is what every
   *   mutating request in every pilet attaches, so without it the app renders fine and
   *   then fails on the first thing the visitor tries to do.
   *
   * Device enrollment is deliberately *not* in this set: it is degraded service, not a
   * broken app, so it stays caught to null and the visitor proceeds signed in.
   */
  failed: StartupFailure | null;
  /**
   * Non-null once the handshake settled fine but the app must still not mount —
   * currently only a members-only library reached by an anonymous visitor whom we
   * could not redirect. The shell shows the same page as {@link failed}, in its
   * not-an-error form.
   */
  blocked: StartupBlock | null;
}

const AuthContext = React.createContext<AuthState>({ user: null, ready: false, device: null, failed: null, blocked: null })

/** Auth state for the shell's own chrome (nav filtering, route guards). */
export const useAuth = (): AuthState => React.useContext(AuthContext)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useUser()
  const [, setSession] = useSession()
  const [device, setDevice] = React.useState<DeviceEnrollmentResponse | null>(null)
  const [ready, setReady] = React.useState(false)
  const [failed, setFailed] = React.useState<StartupFailure | null>(null)
  const [blocked, setBlocked] = React.useState<StartupBlock | null>(null)

  React.useEffect(() => {
    let cancelled = false
    let heartbeatId: number | undefined
    // Set once we have started a full-page navigation to the sign-in app. The
    // handshake is over at that point, but the shell must NOT be released: `ready`
    // stays false so the loading screen holds until the page actually goes away,
    // rather than flashing an empty private library on the way out.
    let leaving = false

    /** Give up on this page load: the shell will render the startup error page. */
    const fail = (reason: StartupFailure, detail: string) => {
      if (cancelled) return
      console.warn(`[auth] startup handshake failed (${reason}): ${detail}`)
      // A shell that never starts is the one fault nobody can report from inside the
      // app, so it goes to telemetry directly. `trackError` reports regardless of
      // consent — this is a crash, not audience measurement.
      trackError(new Error(`Startup handshake failed: ${detail}`), { startup_failure: reason })
      setUser(null)
      setSession(null)
      setDevice(null)
      resetListManifest()
      setFailed(reason)
    }

    // Both halves of the handshake, in flight together rather than chained: the domain
    // configuration is needed whether or not anyone is signed in (it carries the CSRF
    // token every pilet's mutating requests attach), so waiting for whoami first would
    // add a round trip to the one screen the visitor is staring at.
    //
    // Neither call is a new request. `loadWhoami` is the very one the shell started at
    // module scope to publish the tenant to telemetry (see auth/whoami.ts and
    // src/index.tsx), and `loadDomainConfiguration` is the one `createConfigApi` started
    // at `createInstance`; both hand out a single shared promise.
    Promise.all([
      // whoami never rejects — it reports "we could not find out" as a status instead.
      loadWhoami(),
      // The configuration does reject, on a non-200 or a dead network. Folded into a
      // null here so one settled tuple can be classified in one place below (and so the
      // shared promise always has a handler attached, whoever else awaits it).
      loadDomainConfiguration().then(config => config, (err: unknown) => {
        console.warn('[auth] domain configuration request failed', err)
        return null
      }),
    ])
      // `'user'` is the identity every list/toggle already reads; `'session'` carries
      // the whole whoami (roles, quota, and the tenant) for pilets that gate on a role
      // or show session diagnostics. Both are written from this one fetch so they can
      // never disagree.
      .then(async ([whoami, config]) => {
        if (cancelled) return
        // Only 200 and 403 are answers. Everything else is "we do not know", which must
        // not be rounded down to "anonymous" — see AuthState.failed.
        if (whoami.status !== 200 && whoami.status !== 403) {
          return fail('whoami', whoami.status === 0
            ? 'whoami request never reached the server'
            : `whoami answered ${whoami.status}`)
        }
        if (!config) {
          return fail('configuration', 'domain configuration unavailable')
        }
        // A known anonymous visitor — the 403, or a 200 the backend sent without a
        // profile. A stale persisted identity/session must not outlive it.
        if (!whoami.authenticated || !whoami.body) {
          setUser(null)
          setSession(null)
          setDevice(null)
          // Saved lists are per-account and live on the server now: an anonymous
          // visitor has none, and the toggles hide rather than writing anywhere local.
          resetListManifest()
          // A private library has nothing to show an anonymous visitor: every
          // content request is refused, so the app would render as a set of empty
          // rows with no explanation. Send them to sign in instead.
          //
          // Deliberately NOT a startup failure: nothing is broken, and the error
          // page's reload button would only produce the same anonymous session.
          // Deliberately gated on being anonymous too — a private domain is normal
          // for a signed-in member, who must never be bounced out of it.
          if (config.private) {
            leaving = redirectToLoginOnce()
            // The redirect was suppressed by the loop guard, so this page load is
            // staying put. Stop on the sign-in page rather than mounting a library
            // whose every request will be refused: empty rows with no explanation
            // read as a broken app, which is the one impression a working private
            // library must not give.
            if (!leaving) setBlocked('private')
          } else {
            clearLoginRedirectMark()
          }
          return
        }
        // Reaching a normal start clears the loop guard, so a session lost later in
        // this same tab can be redirected again. Here rather than at the top of the
        // handshake: it must only clear on the path that did NOT redirect.
        clearLoginRedirectMark()
        setUser(whoami.body.profile)
        setSession(whoami.body)
        // Load the saved-list membership manifest for this account — one request the
        // whole app shares, so the heart and clock on every tile can render without
        // each costing a round trip. Fire-and-forget on purpose: it never rejects, and
        // it is deliberately NOT part of the startup gate — a slow manifest must not
        // hold up first paint, the toggles simply arrive inactive and correct
        // themselves. Started before device enrollment is awaited so it overlaps it.
        loadListManifest()
        // Authenticated: enroll this browser as a device before releasing the UI,
        // so first paint can act on the outcome — the same reason we block on whoami.
        // The CSRF token this POST needs comes from the shell's shared domain
        // configuration, and is stable for the page's life so the heartbeat below
        // reuses it.
        //
        // An enrollment failure must not strand a signed-in visitor, so it is caught
        // to null and the UI proceeds signed in.
        const enrolled = await enrollDevice(config.csrf_token).catch(err => {
          console.warn('[auth] device enrollment failed', err)
          return null
        })
        if (cancelled) return
        setDevice(enrolled)
        // Tag telemetry with the opaque device id so a crash can be counted per
        // browser rather than per report. Never the profile — see setTelemetryUser.
        // A no-op until analytics consent is granted, and harmless when enrollment
        // failed (there is simply no id to attach).
        setTelemetryUser(getDeviceId())
        // Keep this device's session "active": ping the heartbeat every 30s for the
        // life of the shell. Fire-and-forget — a missed beat is logged and the loop
        // continues. Needs the CSRF token AND a successful enrollment (the heartbeat
        // targets the backend-generated id it stored), so it only starts once both hold.
        if (enrolled) {
          const csrfToken = config.csrf_token
          heartbeatId = window.setInterval(() => {
            sendDeviceHeartbeat(csrfToken).catch(err =>
              console.warn('[auth] device heartbeat failed', err))
          }, DEVICE_HEARTBEAT_INTERVAL_MS)
        }
      })
      // Neither awaited promise rejects any more, but the handler above still runs
      // arbitrary code — and a throw there would otherwise leave `ready` false forever,
      // parking every visitor on the loading screen. Route it to the error page instead
      // of into a silently anonymous session.
      .catch(err => fail('unknown', err instanceof Error ? err.message : String(err)))
      // `leaving` holds the gate shut when a redirect to the sign-in app is under
      // way: the handshake finished, but releasing the shell would paint an empty
      // private library for the frame or two before the browser leaves the page.
      .finally(() => { if (!cancelled && !leaving) setReady(true) })
    return () => {
      cancelled = true
      if (heartbeatId !== undefined) window.clearInterval(heartbeatId)
    }
    // Once per shell load: the session doesn't change under us within a page life.
  }, [])

  return <AuthContext.Provider value={{ user, ready, device, failed, blocked }}>{children}</AuthContext.Provider>
}
